import { useCallback, useMemo, useRef } from 'react';

import {
  useFlagIndexPageQuery,
  useListMetricsQuery,
  useListWorkflowInstancesQuery,
  usePulsesQuery,
} from '@spotify/confidence-graphql';

import {
  normalizeExperimentResults,
  normalizeFlagResults,
  normalizeMetricResults,
  normalizePulseResults,
} from './normalizers';
import {
  EXPERIMENT_TYPES,
  type UseResourceSearchOptions,
  type UseResourceSearchResult,
} from './types';

const PAGE_SIZE = 20;

const WORKFLOW_PARENT_FILTERS: Record<string, string> = {
  abtest: 'parent:"workflows/abtest"',
  rollout: 'parent:"workflows/rollout"',
  analysis: 'parent:"workflows/analysis" OR parent:"workflows/reanalysis"',
};

const buildExperimentFilter = (
  filterString: string,
  activeTypes: string[],
): string => {
  const selectedWorkflowTypes = EXPERIMENT_TYPES.filter(t =>
    activeTypes.includes(t),
  );

  if (selectedWorkflowTypes.length === 0) return filterString;

  const parentFilter = selectedWorkflowTypes
    .map(t => WORKFLOW_PARENT_FILTERS[t])
    .join(' OR ');

  const parts = [filterString, `(${parentFilter})`].filter(Boolean);
  return parts.join(' AND ');
};

export const useResourceSearch = ({
  filterString,
  activeTypes,
  skip,
  pageSize = PAGE_SIZE,
}: UseResourceSearchOptions): UseResourceSearchResult => {
  const includeFlags = activeTypes.includes('flag');
  const includeMetrics = activeTypes.includes('metric');
  const includePulses = activeTypes.includes('pulse');
  const includeExperiments = EXPERIMENT_TYPES.some(t =>
    activeTypes.includes(t),
  );

  const experimentFilter = useMemo(
    () => buildExperimentFilter(filterString, activeTypes),
    [filterString, activeTypes],
  );

  const flagsResult = useFlagIndexPageQuery({
    variables: {
      filter: filterString || undefined,
      pageSize,
      orderBy: 'createTime:desc',
    },
    skip: skip || !includeFlags,
  });

  const metricsResult = useListMetricsQuery({
    variables: {
      filter: filterString || undefined,
      pageSize,
      orderBy: 'displayName:asc',
    },
    skip: skip || !includeMetrics,
  });

  const experimentsResult = useListWorkflowInstancesQuery({
    variables: {
      filter: experimentFilter || undefined,
      pageSize,
      orderBy: 'updateTime:desc',
    },
    skip: skip || !includeExperiments,
  });

  const pulsesResult = usePulsesQuery({
    variables: {
      filter: filterString || undefined,
      pageSize,
    },
    skip: skip || !includePulses,
  });

  const isLoading =
    (includeFlags && !skip && flagsResult.loading) ||
    (includeMetrics && !skip && metricsResult.loading) ||
    (includeExperiments && !skip && experimentsResult.loading) ||
    (includePulses && !skip && pulsesResult.loading);

  const flagsData = flagsResult.data ?? flagsResult.previousData;
  const metricsData = metricsResult.data ?? metricsResult.previousData;
  const experimentsData =
    experimentsResult.data ?? experimentsResult.previousData;
  const pulsesData = pulsesResult.data ?? pulsesResult.previousData;

  const results = useMemo(() => {
    const flags = includeFlags ? normalizeFlagResults(flagsData) : [];
    const metrics = includeMetrics ? normalizeMetricResults(metricsData) : [];
    const experiments = includeExperiments
      ? normalizeExperimentResults(experimentsData)
      : [];
    const pulses = includePulses ? normalizePulseResults(pulsesData) : [];
    return [...experiments, ...flags, ...metrics, ...pulses];
  }, [
    flagsData,
    metricsData,
    experimentsData,
    includeFlags,
    includeMetrics,
    includeExperiments,
    includePulses,
    pulsesData,
  ]);

  const flagsNextToken =
    includeFlags && flagsResult.data?.flags?.__typename !== 'Error'
      ? flagsResult.data?.flags?.nextPageToken
      : undefined;
  const metricsNextToken =
    includeMetrics && metricsResult.data?.metrics?.__typename !== 'Error'
      ? metricsResult.data?.metrics?.nextPageToken
      : undefined;
  const experimentsNextToken =
    includeExperiments &&
    experimentsResult.data?.workflowInstances?.__typename !== 'Error'
      ? experimentsResult.data?.workflowInstances?.nextPageToken
      : undefined;
  const pulsesNextToken =
    includePulses && pulsesResult.data?.pulses?.__typename !== 'Error'
      ? pulsesResult.data?.pulses?.nextPageToken
      : undefined;
  const hasNextPage =
    !!flagsNextToken ||
    !!metricsNextToken ||
    !!experimentsNextToken ||
    !!pulsesNextToken;

  const isLoadingMore =
    (includeFlags && flagsResult.networkStatus === 3) ||
    (includeMetrics && metricsResult.networkStatus === 3) ||
    (includeExperiments && experimentsResult.networkStatus === 3) ||
    (includePulses && pulsesResult.networkStatus === 3);

  const loadingMoreRef = useRef(false);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;

    const promises: Promise<unknown>[] = [];
    if (flagsNextToken) {
      promises.push(
        flagsResult.fetchMore({ variables: { pageToken: flagsNextToken } }),
      );
    }
    if (metricsNextToken) {
      promises.push(
        metricsResult.fetchMore({
          variables: { pageToken: metricsNextToken },
        }),
      );
    }
    if (experimentsNextToken) {
      promises.push(
        experimentsResult.fetchMore({
          variables: { pageToken: experimentsNextToken },
        }),
      );
    }
    if (pulsesNextToken) {
      promises.push(
        pulsesResult.fetchMore({ variables: { pageToken: pulsesNextToken } }),
      );
    }
    Promise.all(promises).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [
    flagsNextToken,
    metricsNextToken,
    experimentsNextToken,
    pulsesNextToken,
    flagsResult,
    metricsResult,
    experimentsResult,
    pulsesResult,
  ]);

  const isRefetching = isLoading && results.length > 0;

  return {
    results,
    isLoading,
    isRefetching,
    isEmpty: !isLoading && results.length === 0,
    hasNextPage,
    loadMore,
    isLoadingMore,
  };
};
